import { getTextToSpeech } from '@/services/textToSpeech'
import store from '../store'
import VoiceState from './voiceConstants';
import ElevenLabsTextToSpeech from '@/services/11labs.js'
import ConversationHandlerClient from './aws-iot/ConversationHandlerClient';
import { getVoiceNameAndLocaleFromLanguage } from './utils';

class AudioUtil {
  constructor() {
    this.audioQueue = [];
    this.isPlaying = false;
    this.textBuffer = [];
    this.isRequestScheduled = false;
    this.isLastChunk = false;
    this.currentAudio = null;
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)(); // Use AudioContext for better iOS compatibility
    this.startTime = null
    this.endTime = null
    this.lastPlayedAudioIndex = 0;
  }

  init() {
    this.endAudio();
    this.currentAudio = null;
  }

  allAudiosPlayed() {
    return this.eLBESocketsEnabled()
      ? this.lastPlayedAudioIndex >= this.audioQueue.length
      : this.audioQueue.length === 0
  }

  eLBESocketsEnabled() {
    return store.getters.voiceTTSProvider === '11labs' && store.getters.elevenLabsWebSocketEnabled && store.getters.enableBackendElevenLabsSocket;
  }

  initElevenLabsWebSocket() {
    if(store.getters.voiceTTSProvider === '11labs' && store.getters.elevenLabsWebSocketEnabled && !store.getters.enableBackendElevenLabsSocket) {
      ElevenLabsTextToSpeech.init({
        onMessageCallback : (message) => {
          this.audioQueue.push(JSON.parse(message).audio)
          this.playAudioFromQueue()
        },
        onMessageCompleteCallback : () => {
          this.isRequestScheduled = false
          if(this.textBuffer.length > 0) {
            this.scheduleRequest()
          }
        },
        onCloseCallback : () => {
          this.endAudio()
          store.dispatch('updateVoiceModeActive', false);
        }
      })
    }
  }

  playText(htmlText, isLastChunk) {
    store.state.isLastChunkPlayed = false;
    store.state.currentVoiceState = VoiceState.SPEAKING
    this.isLastChunk = isLastChunk || false;

    if(this.allAudiosPlayed() && this.textBuffer.length === 0 && this.isLastChunk && !this.isPlaying && !this.isRequestScheduled && !htmlText) {
      store.state.isLastChunkPlayed = true;
      store.state.currentVoiceState = VoiceState.NONE
    }

    if (htmlText) {
      const text = this.extractTextFromHTML(htmlText);
      this.textBuffer.push(text);
      this.scheduleRequest();
    }

    this.initElevenLabsWebSocket()

  }

  // Function to handle audio data received in MQTT topic
  handleAudioData(audioData) {
    if(audioData.audio) {
      this.audioQueue[audioData.audioIndex] = audioData
      this.playAudioFromQueue()
      this.isRequestScheduled = false
      if (this.textBuffer.length > 0) this.scheduleRequest()
    }
  }

  endAudio() {
    /**
     * In case of BE socket, the lastPlayedAudioIndex is kept in sync with the indexes of 
     * audios received from backend, therefore, not emptying the queue as it will give undefined.
     */
    if (!this.eLBESocketsEnabled()) this.audioQueue = []
    this.isPlaying = false
    this.textBuffer = []
    this.isRequestScheduled = false
    this.isLastChunk = false
    store.state.isLastChunkPlayed = false
    this.startTime = null
    this.endTime = null
    if (store.state.currentVoiceState === VoiceState.SPEAKING) {
      store.state.currentVoiceState = VoiceState.NONE
    }
    if (this.currentAudio) {
      this.currentAudio.stop() // Stop the current audio
      this.currentAudio = null
    }
  }

  async getAudio(text) {
    try {
      if(this.startTime === null) {
        this.startTime = performance.now()
      }
      if(store.getters.voiceTTSProvider === '11labs') {
        const response = await getTextToSpeech({
          botResponseText: text,
        });
        const audioArray = new Uint8Array(response.content.data);
        return audioArray.buffer; // Return as ArrayBuffer
      }
      else {
        const {voiceName , langCode} = getVoiceNameAndLocaleFromLanguage()
        const audioString = await getTextToSpeech({
          botResponseText: text,
          voiceName: voiceName, 
          langCode: langCode
        });
        return audioString;
      }
    } catch (error) {
      console.error('Error getting audio:', error);
      throw error;
    }
  }

  async scheduleRequest() {
    if (this.isRequestScheduled || this.textBuffer.length === 0) {
      return;
    }

    this.isRequestScheduled = true;

    const textToProcess = this.textBuffer.shift()
    if(store.getters.voiceTTSProvider === '11labs' && store.getters.elevenLabsWebSocketEnabled) {
      if(store.getters.enableBackendElevenLabsSocket) {
        if(ConversationHandlerClient.liveKitRoom) {
          ConversationHandlerClient.liveKitRoom.localParticipant.sendText(textToProcess);
        } else {
          // Refresh audio index for new connection
          this.lastPlayedAudioIndex = 0;
          await ConversationHandlerClient.initBackendConversationHandler();
          ConversationHandlerClient.liveKitRoom.localParticipant.sendText(textToProcess);
        }
      } else {
        if (this.startTime === null) this.startTime = performance.now()
        ElevenLabsTextToSpeech.sendMessage(textToProcess)
      }
    }
    else {
      this.getAudio(textToProcess)
        .then((audioUrl) => {
          this.audioQueue.push(audioUrl);
          this.playAudioFromQueue();
        })
        .catch((error) => {
          this.endAudio();
          store.dispatch('updateVoiceModeActive', false);
          console.error('Error scheduling request:', error);
        })
        .finally(() => {
          this.isRequestScheduled = false;
          if (this.textBuffer.length > 0) {
            this.scheduleRequest();
          }
        });
      }
  }

  playAudioFromQueue() {
    if (
      !this.allAudiosPlayed() &&
      !this.isPlaying &&
      store.state.isVoiceModeActive
    ) {
      if (store.state.currentVoiceState != VoiceState.SPEAKING) {
        this.endAudio()
        return
      }
      let audioString = ''
      if (this.eLBESocketsEnabled()) {
        const audioToPlay = this.audioQueue[this.lastPlayedAudioIndex]
        if (!audioToPlay || !audioToPlay.audio) return
        this.isPlaying = true
        audioString = audioToPlay.audio
      } else {
        this.isPlaying = true
        audioString = this.audioQueue.shift()
      }
      this.playAudio(audioString)
        .then(() => {
          this.lastPlayedAudioIndex++
          this.isPlaying = false
          this.playAudioFromQueue()
        })
        .catch((error) => {
          console.error('Error playing audio:', error)
          this.isPlaying = false
        })
    } else if (
      this.allAudiosPlayed() &&
      this.textBuffer.length === 0 &&
      this.isLastChunk &&
      !this.isPlaying &&
      !this.isRequestScheduled
    ) {
      store.state.isLastChunkPlayed = true
      store.state.currentVoiceState = VoiceState.NONE
    }
  }


  async playAudio(audioString) {
    return new Promise((resolve, reject) => {
      let audioData = audioString;
      if (
        (store.getters.voiceTTSProvider === '11labs' && store.getters.elevenLabsWebSocketEnabled) || 
        store.getters.voiceTTSProvider === 'google'
      ) {
        audioData = this.base64ToArrayBuffer(audioString)
      }
      this.audioContext.decodeAudioData(audioData, (buffer) => {
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.onended = resolve;
        source.start(0);
        this.currentAudio = source;
      }, reject);
      if(this.endTime === null) {
        this.endTime = performance.now()
        const duration = (this.endTime - this.startTime) / 1000
        console.log(`Completed in ${duration.toFixed(3)} seconds`);
      }
    });
  }

  base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  extractTextFromHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    let textContent =  doc.body.textContent || '';
    const normalizationPhrases = store.getters.normalizationPhrases 
    for (const [key, value] of Object.entries(normalizationPhrases)) {
      const regex = new RegExp(`\\b${key}\\b`, 'gi');
      textContent = textContent.replace(regex, value);
    } 
    return textContent
  }
}

export default new AudioUtil();
